import React from 'react';
import { Card, Table, Alert } from 'react-bootstrap';

interface BingoProbabilityProps {
  count: number;
  names?: string;
  type?: 'numbers' | 'names';
}

const BingoProbability: React.FC<BingoProbabilityProps> = ({ count, names, type }) => {
  console.log("[DEBUG_LOG] BingoProbability - Rendering for count:", count, "type:", type);

  const namesList = type === 'names' ? names?.split(',').map(n => n.trim()).filter(n => n !== '') || [] : [];
  const uniqueNamesCount = new Set(namesList).size;
  console.log("[DEBUG_LOG] BingoProbability - Unique names count:", uniqueNamesCount);

  const isInsufficientNames = type === 'names' && uniqueNamesCount < 24;
  console.log("[DEBUG_LOG] BingoProbability - isInsufficientNames:", isInsufficientNames);

  // Probabilidades baseadas em cartela 5x5 (24 números + 1 livre)
  // Valores aproximados para Bingo de 75 bolas (se for tipo numbers)
  // Se for nomes, o universo de "bolas" é o número total de nomes únicos.
  const poolSize = type === 'names' ? (uniqueNamesCount > 24 ? uniqueNamesCount : 24) : 75;
  
  // Fórmulas simplificadas para fins de demonstração (estimativas estatísticas comuns)
  const calculateRounds = (probability: number, objectiveType: 'full' | 'line') => {
    console.log(`[DEBUG_LOG] BingoProbability.calculateRounds - objectiveType: ${objectiveType}, probability: ${probability}, count: ${count}, poolSize: ${poolSize}`);
    
    if (count <= 0) {
      console.log("[DEBUG_LOG] BingoProbability.calculateRounds - count is 0 or less, returning 0");
      return 0;
    }

    if (objectiveType === 'full') {
      const base50 = (73 / 75) * poolSize;
      const base80 = (74.5 / 75) * poolSize;

      if (probability === 0.5) {
        return Math.max(24, Math.round(base50 - (base50 * 0.1) * Math.log10(count)));
      } else {
        return Math.max(24, Math.round(base80 - (base80 * 0.05) * Math.log10(count)));
      }
    } else {
      const base50 = (41 / 75) * poolSize;
      const base80 = (54 / 75) * poolSize;

      if (probability === 0.5) {
        return Math.max(5, Math.round(base50 - (base50 * 0.3) * Math.log10(count)));
      } else {
        return Math.max(8, Math.round(base80 - (base80 * 0.2) * Math.log10(count)));
      }
    }
  };

  const getBadgeColor = (val: number, type: 'full' | 'line') => {
    if (type === 'full') {
      return val > poolSize * 0.9 ? 'danger' : val > poolSize * 0.7 ? 'warning' : 'success';
    }
    return val > poolSize * 0.6 ? 'danger' : val > poolSize * 0.4 ? 'warning' : 'success';
  };

  const full50 = calculateRounds(0.5, 'full');
  const full80 = calculateRounds(0.8, 'full');
  const line50 = calculateRounds(0.5, 'line');
  const line80 = calculateRounds(0.8, 'line');

  return (
    <div className="p-2 rounded-3" style={{ backgroundColor: 'var(--bingo-card-bg)' }}>
      {count <= 0 && (
        <Alert variant="warning" className="py-2 mb-3 border-0 shadow-sm">
          <small><strong>Atenção:</strong> Adicione ao menos uma cartela para visualizar as probabilidades de ganho.</small>
        </Alert>
      )}

      {isInsufficientNames && (
        <Alert variant="danger" className="py-2 mb-3 border-0 shadow-sm">
          <small><strong>Aviso:</strong> Nomes insuficientes ({uniqueNamesCount}/24). O jogo requer ao menos 24 nomes únicos.</small>
        </Alert>
      )}
      
      <div className="d-flex justify-content-between align-items-center mb-3 px-2">
        <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: 'var(--bingo-bg-alt)', color: 'var(--bingo-text)', border: '1px solid var(--bingo-border)' }}>
          {count} {count === 1 ? 'cartela' : 'cartelas'}
        </span>
        <span className="badge rounded-pill px-3 py-2" style={{ backgroundColor: 'var(--bingo-bg-alt)', color: 'var(--bingo-text)', border: '1px solid var(--bingo-border)' }}>
          {type === 'names' ? `${poolSize} nomes` : '75 números'}
        </span>
      </div>

      <Table responsive size="sm" className="mb-0 align-middle border rounded-3 overflow-hidden" style={{ '--bs-table-bg': 'var(--bingo-card-bg)' } as React.CSSProperties}>
        <thead style={{ backgroundColor: 'var(--bingo-bg-alt)' }}>
          <tr>
            <th className="ps-3 py-3 border-0" style={{ color: 'var(--bingo-text)' }}>Objetivo</th>
            <th className="text-center py-3 border-0" style={{ color: 'var(--bingo-text)' }}>50% Chance</th>
            <th className="text-center py-3 border-0" style={{ color: 'var(--bingo-text)' }}>80% Chance</th>
          </tr>
        </thead>
        <tbody>
          <tr className="border-bottom">
            <td className="ps-3 py-3"><span className="fw-bold" style={{ color: 'var(--bingo-text)' }}>Cartela Cheia</span></td>
            <td className="text-center py-3">
              <span className={`badge bg-${getBadgeColor(full50, 'full')} rounded-pill px-3 py-2`}>{full50}</span>
            </td>
            <td className="text-center py-3">
              <span className={`badge bg-${getBadgeColor(full80, 'full')} rounded-pill px-3 py-2`}>{full80}</span>
            </td>
          </tr>
          <tr>
            <td className="ps-3 py-3"><span className="fw-bold" style={{ color: 'var(--bingo-text)' }}>Linha ou Coluna</span></td>
            <td className="text-center py-3">
              <span className={`badge bg-${getBadgeColor(line50, 'line')} rounded-pill px-3 py-2`}>{line50}</span>
            </td>
            <td className="text-center py-3">
              <span className={`badge bg-${getBadgeColor(line80, 'line')} rounded-pill px-3 py-2`}>{line80}</span>
            </td>
          </tr>
        </tbody>
      </Table>
      
      <div className="mt-4 p-3 rounded-3 border-0" style={{ backgroundColor: 'var(--bingo-bg-alt)' }}>
        <p className="mb-0" style={{ fontSize: '0.85rem', lineHeight: '1.4', color: 'var(--bingo-text-muted)' }}>
          <strong>Dica:</strong> Os valores representam o número estimado de sorteios necessários. Gerar mais cartelas aumenta a probabilidade de completar objetivos em menos rodadas.
        </p>
      </div>
    </div>
  );
};

export default BingoProbability;
